/**
 * Trial dialer helpers (desktop GlobalDialer + mobile dialer).
 *
 * Trials may call only the AI demo line and their own verified numbers
 * (server/lib/trialOutboundAllowlist). This renders the quick-dial actions for
 * those, the free test calls left today, and, when the typed number is anything
 * else, an inline limit note with "Start plan" (DirectUpgradeSheet trial convert)
 * and "Call AI demo".
 */
import { useQuery } from '@tanstack/react-query';
import { Phone, Sparkles } from 'lucide-react';
import { useDirectUpgrade } from '@/components/billing/DirectUpgradeSheet';
import {
  isTrialAllowedDial, testCallsLeftLabel, TRIAL_INLINE_LIMIT_MESSAGE, type FreeTestCallOptions,
} from '@/lib/freeTestCalls';

export function useTrialTestCalls(enabled = true) {
  return useQuery<FreeTestCallOptions>({ queryKey: ['/api/voice/test-calls'], staleTime: 60 * 1000, enabled });
}

/** "3 of 5 free test calls left today" for a trial dialer. */
export function TrialTestCallsLeft({ testCalls, className }: {
  testCalls: FreeTestCallOptions | null | undefined;
  className?: string;
}) {
  const label = testCallsLeftLabel(testCalls);
  if (!label) return null;
  const out = (testCalls?.remainingToday ?? 0) <= 0;
  return (
    <p
      className={`text-xs tabular-nums ${out ? 'text-destructive' : 'text-muted-foreground'} ${className ?? ''}`}
      role="status"
      data-testid="text-trial-test-calls-left"
    >
      {label}
    </p>
  );
}

/**
 * The trial notice under the number field: shown the moment the typed number
 * is outside the allowlist, with "Start plan" (trial convert) and "Call AI demo".
 */
export function TrialDestinationNotice({ fullNumber, testCalls, source, onCallDemo }: {
  fullNumber: string;
  testCalls: FreeTestCallOptions | null | undefined;
  source: string;
  /** Dials the AI demo line; the button hides without it or without a demo line. */
  onCallDemo?: (e164: string) => void;
}) {
  const { available, openDirectUpgrade } = useDirectUpgrade();
  const typed = (fullNumber || '').replace(/\D/g, '').length >= 8;
  if (!typed || !testCalls || isTrialAllowedDial(fullNumber, testCalls)) return null;
  const demo = testCalls.demo;
  const startPlan = () => {
    if (available) openDirectUpgrade({ feature: 'voice', source });
    else window.location.href = '/settings/billing';
  };
  return (
    <div
      className="rounded-xl border border-brand-lime bg-brand-lime-soft p-3 text-xs text-brand-ink dark:text-brand-lime-soft-foreground"
      role="status"
      data-testid="trial-destination-notice"
    >
      <p className="leading-snug">{TRIAL_INLINE_LIMIT_MESSAGE}</p>
      <div className="mt-2 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={startPlan}
          className="inline-flex items-center gap-1.5 min-h-[36px] rounded-full px-3 font-semibold text-brand-ink bg-brand-lime hover:bg-brand-lime-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          data-testid="button-trial-start-plan"
        >
          <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
          Start plan
        </button>
        {demo && onCallDemo && (
          <button
            type="button"
            onClick={() => onCallDemo(demo.number)}
            disabled={demo.remainingToday <= 0}
            className="inline-flex items-center gap-1.5 min-h-[36px] rounded-full px-3 font-semibold border border-brand-forest text-brand-forest hover:bg-brand-forest/10 disabled:opacity-50 dark:border-brand-lime dark:text-brand-lime focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            data-testid="button-trial-call-ai-demo"
          >
            <Phone className="h-3.5 w-3.5" aria-hidden="true" />
            Call AI demo
          </button>
        )}
      </div>
    </div>
  );
}

/** Quick-dial actions for a trial: the AI demo line and "Call my phone (test)". */
export function TrialQuickDials({ testCalls, onDial, disabled }: {
  testCalls: FreeTestCallOptions | null | undefined;
  onDial: (e164: string) => void;
  disabled?: boolean;
}) {
  if (!testCalls) return null;
  const verified = testCalls.numbers.filter((n) => n.kind === 'verified');
  if (!testCalls.demo && verified.length === 0) return null;
  const chip = 'inline-flex items-center gap-1.5 min-h-[32px] px-2.5 rounded-full text-xs font-medium disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
  return (
    <div className="flex flex-wrap gap-2" data-testid="trial-quick-dials">
      {testCalls.demo && (
        <button
          type="button"
          onClick={() => onDial(testCalls.demo!.number)}
          disabled={disabled || testCalls.demo.remainingToday <= 0}
          className={`${chip} bg-emerald-50 text-emerald-800 hover:bg-emerald-100 dark:bg-emerald-500/10 dark:text-emerald-300`}
          data-testid="button-try-ai-demo"
        >
          <Phone className="h-3.5 w-3.5" aria-hidden="true" />
          Try the AI demo
        </button>
      )}
      {verified.map((n) => (
        <button
          key={n.number}
          type="button"
          onClick={() => onDial(n.number)}
          disabled={disabled || (n.remainingToday ?? 1) <= 0}
          className={`${chip} bg-sky-50 text-sky-800 hover:bg-sky-100 dark:bg-sky-500/10 dark:text-sky-300`}
          aria-label={`Call my phone (test), ${n.number}`}
          data-testid={`button-call-my-phone-${n.number}`}
        >
          <Phone className="h-3.5 w-3.5" aria-hidden="true" />
          Call my phone (test)
        </button>
      ))}
    </div>
  );
}
